<template>
  <div class="progress-modal-overlay" @click="$emit('close')">
    <div class="progress-modal" @click.stop>
      <div class="progress-header">
        <h3><i class="fas fa-seedling"></i> Generation in Progress</h3>
        <button class="close-btn" @click="$emit('close')">
          <i class="fas fa-times"></i>
        </button>
      </div>
      
      <div class="progress-content">
        <div class="progress-circle">
          <svg viewBox="0 0 100 100">
            <circle 
              cx="50" 
              cy="50" 
              r="40" 
              stroke="#e9ecef" 
              stroke-width="8" 
              fill="none"
            />
            <circle 
              cx="50" 
              cy="50" 
              r="40" 
              stroke="#28a745" 
              stroke-width="8" 
              fill="none"
              :stroke-dasharray="circumference"
              :stroke-dashoffset="dashOffset"
              class="progress-arc"
            />
          </svg>
          <div class="progress-text">
            <span class="progress-percent">{{ Math.round(progress.percent || 0) }}%</span>
            <span class="progress-step">{{ progress.step || 'Initialization' }}</span>
          </div>
        </div>
        
        <div class="progress-details">
          <div class="current-task">
            <h4>{{ progress.currentTask || 'Preparing...' }}</h4>
            <p>{{ progress.message || 'Please wait' }}</p>
          </div>
          
          <div class="progress-stats" v-if="progress.stats">
            <div class="stat-item">
              <i class="fas fa-users"></i>
              <span>{{ progress.stats.students || 0 }} students</span>
            </div>
            <div class="stat-item">
              <i class="fas fa-clipboard-list"></i>
              <span>{{ progress.stats.items || 0 }} questions</span>
            </div>
            <div class="stat-item">
              <i class="fas fa-chart-line"></i>
              <span>{{ progress.stats.responses || 0 }} responses</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed } from 'vue';

const props = defineProps({
  progress: {
    type: Object,
    default: () => ({})
  }
});

defineEmits(['close']);

const circumference = computed(() => 2 * Math.PI * 40);
const dashOffset = computed(() => {
  const percent = props.progress.percent || 0;
  return circumference.value - (percent / 100) * circumference.value;
});
</script>

<style scoped>
.progress-modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
}

.progress-modal {
  background: white;
  border-radius: 20px;
  width: 90%;
  max-width: 500px;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
  overflow: hidden;
}

.progress-header {
  background: linear-gradient(135deg, #28a745 0%, #20c997 100%);
  color: white;
  padding: 20px 25px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.progress-header h3 {
  margin: 0;
  display: flex;
  align-items: center;
  gap: 10px;
}

.close-btn {
  background: none;
  border: none;
  color: white;
  font-size: 1.2rem;
  cursor: pointer;
  padding: 5px;
  border-radius: 4px;
  transition: background 0.2s ease;
}

.close-btn:hover {
  background: rgba(255, 255, 255, 0.2);
}

.progress-content {
  padding: 30px;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 30px;
}

.progress-circle {
  position: relative;
  width: 120px;
  height: 120px;
}

.progress-circle svg {
  width: 100%;
  height: 100%;
  transform: rotate(-90deg);
}

.progress-arc {
  transition: stroke-dashoffset 0.5s ease;
}

.progress-text {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  text-align: center;
}

.progress-percent {
  display: block;
  font-size: 1.5rem;
  font-weight: bold;
  color: #2c3e50;
}

.progress-step {
  display: block;
  font-size: 0.8rem;
  color: #6c757d;
  margin-top: 4px;
}

.progress-details {
  text-align: center;
  width: 100%;
}

.current-task h4 {
  color: #2c3e50;
  margin-bottom: 8px;
  font-size: 1.1rem;
}

.current-task p {
  color: #6c757d;
  margin-bottom: 20px;
}

.progress-stats {
  display: flex;
  justify-content: center;
  gap: 20px;
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 5px;
  font-size: 0.9rem;
  color: #495057;
}

.stat-item i {
  color: #28a745;
}

@media (max-width: 768px) {
  .progress-modal {
    width: 95%;
    margin: 0 10px;
  }
  
  .progress-content {
    padding: 20px;
  }
  
  .progress-stats {
    flex-direction: column;
    gap: 10px;
  }
}
</style>